import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useMutation } from '@apollo/client';
import { useToast } from 'react-native-toast-notifications';
import PatientAuthService from '@/services/PatientAuthService';
import {
  PatientFormData,
  FormData,
  FormStep,
  PatientFormValidator,
} from '@/config/patient-auth';
import { useUpdatePatientFormDataMutation } from '@/data/data';

interface PatientFormContextType {
  formData: PatientFormData;
  updateForm: (data: Partial<FormData>) => void;
  setCurrentStep: (step: FormStep) => void;
  saveProgress: () => Promise<void>;
  isAutoSaving: boolean;
  lastSavedAt: Date | null;
  canProceedToStep: (step: FormStep) => boolean;
  validateCurrentStep: () => { isValid: boolean; errors: Record<string, string> };
  progressPercentage: number;
  patientName: string;
}

const PatientFormContext = createContext<PatientFormContextType | undefined>(undefined);

export const PatientFormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const toast = useToast();

  // États principaux
  const [formData, setFormData] = useState<PatientFormData>({
    form: {},
    metadata: {
      currentStep: 'form',
      completedSteps: [],
      startedAt: new Date().toISOString(),
      lastModifiedAt: new Date().toISOString(),
      autoSaveEnabled: true,
      validationErrors: {},
    },
  });

  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [patientName, setPatientName] = useState('');
  const autoSaveTimerRef = useRef<number | null>(null);
  const hasUnsavedChanges = useRef(false);

  const [updateFormDataMutation] = useUpdatePatientFormDataMutation({
    fetchPolicy: 'network-only',
  });

  // Charger les données sauvegardées au démarrage
  useEffect(() => {
    const loadSavedData = async () => {
      try {
        // Charger les données du formulaire
        const savedFormData = await PatientAuthService.getFormData();
        if (savedFormData) {
          // S'assurer que la structure est complète avec des valeurs par défaut
          setFormData({
            form: savedFormData.form || {},
            metadata: {
              currentStep: savedFormData.metadata?.currentStep || 'form',
              completedSteps: savedFormData.metadata?.completedSteps || [],
              startedAt: savedFormData.metadata?.startedAt || new Date().toISOString(),
              lastModifiedAt: savedFormData.metadata?.lastModifiedAt || new Date().toISOString(),
              autoSaveEnabled: savedFormData.metadata?.autoSaveEnabled ?? true,
              validationErrors: savedFormData.metadata?.validationErrors || {},
            },
          });
          setLastSavedAt(new Date(savedFormData.metadata?.lastModifiedAt || new Date()));
        }

        // Récupérer les informations patient si disponibles
        const tokenData = await PatientAuthService.getToken();
        if (tokenData && tokenData.patientName) {
          setPatientName(tokenData.patientName);
        } else if (savedFormData?.form?.prenom && savedFormData?.form?.nom) {
          setPatientName(`${savedFormData.form.prenom} ${savedFormData.form.nom}`);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        if (toast && typeof toast.show === 'function') {
          toast.show('Erreur lors du chargement', {
            type: 'danger',
            placement: 'bottom',
          });
        }
      }
    };

    loadSavedData();
  }, []);

  // Auto-save toutes les 30 secondes si il y a des changements non sauvegardés
  useEffect(() => {
    const startAutoSave = () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
      }

      autoSaveTimerRef.current = setInterval(async () => {
        if (hasUnsavedChanges.current && formData.metadata.autoSaveEnabled) {
          await saveProgress();
        }
      }, 30000); // 30 secondes
    };

    if (formData.metadata.autoSaveEnabled) {
      startAutoSave();
    }

    return () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
      }
    };
  }, [formData.metadata.autoSaveEnabled]);

  // Marquer les changements non sauvegardés
  useEffect(() => {
    hasUnsavedChanges.current = true;
  }, [formData]);

  const saveProgress = useCallback(async () => {
    if (isAutoSaving) return; // Éviter les saves concurrents

    try {
      setIsAutoSaving(true);

      // Sauvegarder localement d'abord
      const saveResult = await PatientAuthService.saveFormData(formData);
      if (!saveResult.success) {
        throw new Error(saveResult.error);
      }

      // Sauvegarder sur le serveur si on a un token
      const tokenData = await PatientAuthService.getToken();
      if (tokenData && tokenData.token) {
        console.log('[PatientFormContext] Sauvegarde sur le serveur via GraphQL');
        try {
          const result = await updateFormDataMutation({
            variables: {
              token: tokenData.token,
              formData: {
                form: formData.form,
                metadata: formData.metadata,
              },
            },
          });
          console.log('[PatientFormContext] Sauvegarde serveur réussie:', result);
        } catch (mutationError) {
          console.error(
            '[PatientFormContext] Erreur lors de la sauvegarde serveur:',
            mutationError
          );
          // Ne pas bloquer si la sauvegarde serveur échoue, on a déjà sauvegardé localement
        }
      }

      setLastSavedAt(new Date());
      hasUnsavedChanges.current = false;

      if (toast && typeof toast.show === 'function') {
        toast.show('Progression sauvegardée automatiquement', {
          type: 'success',
          placement: 'bottom',
          duration: 2000,
          animationType: 'slide-in',
        });
      } else {
        console.log('Progression sauvegardée automatiquement');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      if (toast && typeof toast.show === 'function') {
        toast.show('Erreur de sauvegarde', {
          type: 'warning',
          placement: 'bottom',
          duration: 3000,
        });
      }
    } finally {
      setIsAutoSaving(false);
    }
  }, [formData, updateFormDataMutation, isAutoSaving, toast]);

  const updateForm = useCallback((data: Partial<FormData>) => {
    setFormData((prev) => ({
      ...prev,
      form: { ...prev.form, ...data },
      metadata: {
        ...prev.metadata,
        lastModifiedAt: new Date().toISOString(),
        validationErrors: {}, // Clear les erreurs lors de la modification
      },
    }));
  }, []);

  const setCurrentStep = useCallback((step: FormStep) => {
    setFormData((prev) => {
      const completedSteps = [...prev.metadata.completedSteps];
      const steps: FormStep[] = ['form', 'confirmation'];
      const currentStepIndex = steps.indexOf(prev.metadata.currentStep);
      const newStepIndex = steps.indexOf(step);

      // Marquer l'étape actuelle comme complétée si on avance
      if (newStepIndex > currentStepIndex && !completedSteps.includes(prev.metadata.currentStep)) {
        completedSteps.push(prev.metadata.currentStep);
      }

      return {
        ...prev,
        metadata: {
          ...prev.metadata,
          currentStep: step,
          completedSteps,
          lastModifiedAt: new Date().toISOString(),
        },
      };
    });
  }, []);

  const validateCurrentStep = useCallback(() => {
    const currentStep = formData.metadata.currentStep;

    switch (currentStep) {
      case 'form':
        return PatientFormValidator.validateFormStep(formData.form);
      case 'confirmation':
        // Validation globale pour la confirmation
        return PatientFormValidator.validateFormStep(formData.form);
      default:
        return { isValid: true, errors: {} };
    }
  }, [formData]);

  const canProceedToStep = useCallback(
    (targetStep: FormStep) => {
      const steps: FormStep[] = ['form', 'confirmation'];
      const targetIndex = steps.indexOf(targetStep);
      const currentIndex = steps.indexOf(formData.metadata.currentStep);

      // On peut toujours revenir en arrière
      if (targetIndex <= currentIndex) {
        return true;
      }

      // Vérifier que chaque étape précédente est valide
      for (let i = 0; i < targetIndex; i++) {
        const stepToValidate = steps[i];
        let validation;

        switch (stepToValidate) {
          case 'form':
            validation = PatientFormValidator.validateFormStep(formData.form);
            break;
          default:
            validation = { isValid: true, errors: {} };
        }

        if (!validation.isValid) {
          return false;
        }
      }

      return true;
    },
    [formData]
  );

  // Calculer le pourcentage de progression
  const progressPercentage = React.useMemo(() => {
    const steps: FormStep[] = ['form', 'confirmation'];

    // Vérification défensive pour éviter les erreurs si metadata n'est pas complètement initialisé
    if (!formData?.metadata?.completedSteps || !formData?.metadata?.currentStep) {
      return 0;
    }

    const completedCount = formData.metadata.completedSteps.length;
    const currentStepIndex = steps.indexOf(formData.metadata.currentStep);

    // Si on est sur une étape, considérer qu'elle est à moitié complétée
    const currentProgress = currentStepIndex >= 0 ? 0.5 : 0;

    return Math.round(((completedCount + currentProgress) / steps.length) * 100);
  }, [formData?.metadata?.completedSteps, formData?.metadata?.currentStep]);

  const value: PatientFormContextType = {
    formData,
    updateForm,
    setCurrentStep,
    saveProgress,
    isAutoSaving,
    lastSavedAt,
    canProceedToStep,
    validateCurrentStep,
    progressPercentage,
    patientName,
  };

  return <PatientFormContext.Provider value={value}>{children}</PatientFormContext.Provider>;
};

export const usePatientForm = () => {
  const context = useContext(PatientFormContext);
  if (!context) {
    throw new Error('usePatientForm must be used within PatientFormProvider');
  }
  return context;
};
